import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Button, Input } from '../components';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';

export const AdminProfilePage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [debtorIban, setDebtorIban] = useState('');

  useEffect(() => {
    const loadSettings = async () => {
      if (!user || user.role !== 'admin') return;
      try {
        const response = await api.get('/admin/settings/sepa');
        if (response.data?.success) {
          setDebtorIban(response.data.data?.debtor_iban || '');
        }
      } catch (error) {
        console.error('Error loading admin SEPA settings:', error);
        toast.error(t('admin.profile.loadError'));
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [user, t]);

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Card>
          <p className="text-sm text-gray-700">{t('admin.profile.adminOnly')}</p>
        </Card>
      </div>
    );
  }

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);

    try {
      const response = await api.put('/admin/settings/sepa', {
        debtor_iban: debtorIban,
      });
      if (response.data?.success) {
        setDebtorIban(response.data.data?.debtor_iban || debtorIban);
        toast.success(t('admin.profile.saveSuccess'));
      }
    } catch (error: any) {
      console.error('Error saving admin SEPA settings:', error);
      toast.error(error?.response?.data?.message || t('admin.profile.saveError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Card>
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">{t('admin.profile.loading')}</p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin.profile.sepaIban')}
              </label>
              <Input
                value={debtorIban}
                onChange={(e) => setDebtorIban(e.target.value)}
                placeholder={t('admin.profile.sepaIbanPlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('admin.profile.sepaIbanHint')}
              </p>
            </div>

            <div className="flex justify-end">
              <Button type="submit" loading={loading}>
                {t('admin.profile.save')}
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};
